import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useI18n } from '../../../i18n/I18nContext'
import { learningContentService } from '../../../services/learningContent'
import { mentorAdminService } from '../../../services/mentorAdmin'
import type { LearningTemplate, TemplateAssignment, CoachVisibleAttempt, TemplateListFilters, LessonActivityType, CreateTemplatePayload, FindOrGenerateResult } from '../../../types/learningContent'
import type { MentorAdminRecord } from '../../../types/mentorAdmin'
import { EmptyState, ErrorBanner, LoadingState, EntityActionMenu, DetailDrawer, FormModal } from './AdminUiPrimitives'

const ACTIVITY_TYPES: LessonActivityType[] = ['multiple_choice', 'error_spotting', 'short_answer', 'ordering', 'matching', 'pronunciation_prompt', 'checklist', 'reflection']

interface ManualQuestionDraft {
  text: string
  options: string
  correctIndex: string
}

const emptyQuestion = (): ManualQuestionDraft => ({ text: '', options: '', correctIndex: '0' })

interface MatchPairDraft {
  left: string
  right: string
}

const emptyMatchPair = (): MatchPairDraft => ({ left: '', right: '' })

interface TemplateFormState {
  templateKind: 'lesson' | 'quiz' | 'activity' | 'assessment'
  title: string
  description: string
  skill: string
  competency: string
  difficulty: string
  language: string
  estimatedMinutes: string
  activityType: LessonActivityType
  prompt: string
  instructions: string
  tags: string
  questions: ManualQuestionDraft[]
  checklistItems: string
  shortAnswerModelAnswer: string
  shortAnswerMinSentences: string
  errorSpottingText: string
  errorSpottingTargets: string
  orderingItems: string
  matchingPairs: MatchPairDraft[]
  pronunciationPhrases: string
  reflectionQuestions: string
  activityJson: string
  answerKeyJson: string
  status: 'draft' | 'active'
}

const emptyTemplateForm = (): TemplateFormState => ({
  templateKind: 'lesson',
  title: '',
  description: '',
  skill: '',
  competency: '',
  difficulty: '',
  language: 'en',
  estimatedMinutes: '',
  activityType: 'multiple_choice',
  prompt: '',
  instructions: '',
  tags: '',
  questions: [emptyQuestion()],
  checklistItems: '',
  shortAnswerModelAnswer: '',
  shortAnswerMinSentences: '4',
  errorSpottingText: '',
  errorSpottingTargets: '',
  orderingItems: '',
  matchingPairs: [emptyMatchPair(), emptyMatchPair()],
  pronunciationPhrases: '',
  reflectionQuestions: '',
  activityJson: '',
  answerKeyJson: '',
  status: 'active',
})

const titleCase = (value?: string | null) => String(value || '-').replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase())

// Mirrors server learningContentService.ts ACTIVITY_TYPE_TO_KIND so "Edit manually from
// this output" prefills the same templateKind the backend would have used.
const ACTIVITY_TYPE_TO_KIND: Partial<Record<LessonActivityType, TemplateFormState['templateKind']>> = {
  multiple_choice: 'quiz',
  error_spotting: 'activity',
  matching: 'activity',
  ordering: 'activity',
  short_answer: 'lesson',
  reflection: 'lesson',
  checklist: 'activity',
  pronunciation_prompt: 'activity',
}

const VALIDATION_STATUS_LABEL: Record<string, string> = {
  valid: 'Generated successfully',
  repaired: 'Generated, content repaired',
  fallback: 'Generated using fallback content',
  malformed: 'Generation malformed',
}

const VALIDATION_STATUS_HELP: Record<string, string> = {
  valid: 'The AI returned a complete, well-formed lesson — no repair was needed.',
  repaired: 'The AI response was missing or had invalid fields, so it was automatically repaired with safe defaults. Review it before activating.',
  fallback: 'The AI was unavailable, errored, or did not return usable content, so deterministic placeholder content was used instead. Review and edit before activating.',
  malformed: 'The AI did not return a usable lesson at all. Showing what could be recovered — review carefully or edit manually before saving as active.',
}

const FALLBACK_REASON_LABEL: Record<string, string> = {
  provider_not_configured: 'AI provider is not configured',
  provider_timeout: 'AI request timed out',
  provider_error: 'AI request failed',
  malformed_ai_output: "AI response wasn't valid JSON",
  oversized_output: 'AI response was too large and got cut off',
  validation_failed: "AI response didn't include everything this activity type needs",
}

const FALLBACK_REASON_HELP: Record<string, string> = {
  provider_not_configured: 'Set LLM_PROVIDER, LLM_MODEL, and LLM_API_KEY on the server to enable AI generation.',
  provider_timeout: 'The AI provider took too long to respond. Try again, or check the provider status.',
  provider_error: 'The AI provider returned an error. Check server logs for the provider error message.',
  malformed_ai_output: 'The AI replied, but the response could not be parsed as JSON, so no usable content was produced.',
  oversized_output: 'The AI response hit the output token limit before finishing valid JSON. Try a shorter prompt/context or raise LLM_MAX_TOKENS.',
  validation_failed: 'The AI returned JSON, but it was missing fields this activity type requires (e.g. a dictée text with target errors, or a passage with questions).',
}

const SAVE_STATUS_LABEL: Record<string, string> = {
  not_saved: 'Not saved yet',
  saved_draft: 'Saved as draft',
  saved_active: 'Saved as active',
  failed: 'Failed to save',
}

const REUSE_DECISION_LABEL: Record<string, string> = {
  reused_existing: 'Reused existing template',
  generated_new: 'Generated new template',
  regenerated_due_to_quality: 'Regenerated due to quality',
  generated_due_to_gap: 'Generated due to library gap',
  duplicate_rejected: 'Similar lesson reused',
}

function lessonActivityField(lessonActivity: Record<string, unknown> | null | undefined, key: string): unknown {
  return lessonActivity ? (lessonActivity as Record<string, unknown>)[key] : undefined
}

function asText(value: unknown): string {
  return typeof value === 'string' ? value : value == null ? '' : JSON.stringify(value)
}

function asTextList(value: unknown): string[] {
  if (!Array.isArray(value)) return []
  return value.map((entry) => String(entry ?? '').trim()).filter(Boolean)
}

function lines(value: string): string[] {
  return value.split('\n').map((line) => line.trim()).filter(Boolean)
}

function parseErrorTargets(value: string) {
  return lines(value).map((line) => {
    const [left, explanation = ''] = line.split('|').map((part) => part.trim())
    const [text, correction] = left.split(/=>|->/).map((part) => part.trim())
    return { text, correction: correction || text, explanation: explanation || 'Review this correction.' }
  }).filter((target) => target.text)
}

function activeMatchPairs(pairs: MatchPairDraft[]): MatchPairDraft[] {
  return pairs.map((pair) => ({ left: pair.left.trim(), right: pair.right.trim() })).filter((pair) => pair.left || pair.right)
}

// Builds manual-authoring TemplateFormState fields from a generated lessonActivity so
// "Edit manually from this output" reuses the same form/contract as hand authoring
// instead of inventing a second content schema.
function templateFormFromLessonActivity(lessonActivity: Record<string, unknown>): TemplateFormState {
  const activityType = (lessonActivityField(lessonActivity, 'activityType') as LessonActivityType) || 'checklist'
  const activity = (lessonActivityField(lessonActivity, 'activity') as Record<string, unknown>) || {}
  const answerKey = (lessonActivityField(lessonActivity, 'answerKey') as Record<string, unknown>) || {}
  const base = emptyTemplateForm()
  const form: TemplateFormState = {
    ...base,
    templateKind: ACTIVITY_TYPE_TO_KIND[activityType] || 'lesson',
    title: asText(lessonActivityField(lessonActivity, 'lessonTitle')),
    skill: asText(lessonActivityField(lessonActivity, 'skill')),
    difficulty: asText(lessonActivityField(lessonActivity, 'difficulty')),
    estimatedMinutes: lessonActivityField(lessonActivity, 'estimatedMinutes') != null ? String(lessonActivityField(lessonActivity, 'estimatedMinutes')) : '',
    activityType,
    prompt: asText(lessonActivityField(lessonActivity, 'prompt')),
    instructions: asText(lessonActivityField(lessonActivity, 'instructions')),
    status: 'draft',
  }
  if (activityType === 'multiple_choice' && Array.isArray(activity.questions) && activity.questions.length) {
    const answers = (answerKey.questions as Array<Record<string, unknown>> | undefined) || []
    form.questions = (activity.questions as Array<Record<string, unknown>>).map((question) => {
      const options = Array.isArray(question.options) ? question.options.map((option) => String(option)) : []
      const correctAnswer = question.correctAnswer ?? answers.find((entry) => entry.id === question.id)?.correctAnswer
      const correctIndex = options.findIndex((option) => option === correctAnswer)
      return {
        text: asText(question.question ?? question.text),
        options: options.join('\n'),
        correctIndex: String(correctIndex >= 0 ? correctIndex : 0),
      }
    })
  } else {
    if (activityType === 'checklist') {
      form.checklistItems = asTextList(activity.items ?? activity.checklist).join('\n')
    } else if (activityType === 'short_answer') {
      form.prompt = form.prompt || asText(activity.writingPrompt)
      form.checklistItems = asTextList(activity.checklist).join('\n')
      form.shortAnswerModelAnswer = asText(answerKey.modelAnswer ?? activity.modelAnswer)
      form.shortAnswerMinSentences = activity.minSentences != null ? String(activity.minSentences) : '4'
    } else if (activityType === 'reflection') {
      form.reflectionQuestions = asTextList(activity.guidingQuestions ?? activity.questions).join('\n')
      form.shortAnswerModelAnswer = asText(answerKey.sampleResponse ?? answerKey.modelAnswer)
    } else if (activityType === 'error_spotting') {
      const targets = Array.isArray(activity.targetErrors) ? activity.targetErrors : Array.isArray(answerKey.targetErrors) ? answerKey.targetErrors : []
      form.errorSpottingText = asText(activity.text)
      form.errorSpottingTargets = (targets as Array<Record<string, unknown>>).map((target) => {
        const correction = asText(target.correction)
        const explanation = asText(target.explanation)
        return `${asText(target.text)} => ${correction}${explanation ? ` | ${explanation}` : ''}`
      }).filter(Boolean).join('\n')
    } else if (activityType === 'ordering') {
      form.orderingItems = asTextList(activity.items ?? answerKey.correctOrder).join('\n')
    } else if (activityType === 'matching') {
      const pairs = Array.isArray(activity.pairs) ? activity.pairs : Array.isArray(answerKey.pairs) ? answerKey.pairs : []
      form.matchingPairs = (pairs as Array<Record<string, unknown>>).map((pair) => ({
        left: asText(pair.left ?? pair.term ?? pair.prompt),
        right: asText(pair.right ?? pair.match ?? pair.answer),
      })).filter((pair) => pair.left || pair.right)
      if (!form.matchingPairs.length) form.matchingPairs = [emptyMatchPair(), emptyMatchPair()]
    } else if (activityType === 'pronunciation_prompt') {
      form.pronunciationPhrases = asTextList(activity.phrases ?? activity.practiceTargets ?? answerKey.practiceTargets).join('\n')
    }
    // Keep the advanced JSON override empty on prefill so structured edits actually
    // drive the saved lesson. Admins can still paste JSON manually for edge cases.
    form.activityJson = ''
    form.answerKeyJson = ''
  }
  return form
}

// Builds manual-authoring TemplateFormState fields from an EXISTING saved template so
// "Edit content" opens the same form/contract pre-filled with what's actually persisted,
// rather than re-deriving everything from the (possibly stale) lessonActivity payload.
function templateFormFromTemplate(template: LearningTemplate): TemplateFormState {
  const form = templateFormFromLessonActivity(template.lessonActivity || {})
  return {
    ...form,
    templateKind: template.templateKind,
    title: template.title || form.title,
    description: template.coachNotes || '',
    skill: template.skill || form.skill,
    competency: template.competency || '',
    difficulty: template.difficulty || form.difficulty,
    language: template.language || form.language,
    estimatedMinutes: template.estimatedMinutes != null ? String(template.estimatedMinutes) : form.estimatedMinutes,
    activityType: (template.activityType as LessonActivityType) || form.activityType,
    tags: (template.concepts || []).join(', '),
    status: template.status === 'active' ? 'active' : 'draft',
  }
}

const statusClass = (status?: string) => {
  if (status === 'active') return 'status-pill status-pill--approved'
  if (status === 'draft') return 'status-pill status-pill--draft'
  if (status === 'archived') return 'status-pill status-pill--rejected'
  return 'status-pill'
}

type Tab = 'templates' | 'review' | 'assignments' | 'generate'

// Builds the POST /api/learning-content/templates payload from the manual authoring form.
// Pure function so the lessonActivity shape can be unit-verified without rendering the form.
export function buildTemplatePayload(form: TemplateFormState): { payload: CreateTemplatePayload } | { error: string } {
  const title = form.title.trim()
  if (!title) return { error: 'Title is required.' }
  if (!form.skill.trim()) return { error: 'Skill is required.' }
  if (form.activityType === 'multiple_choice') {
    const questions = form.questions.filter((question) => question.text.trim())
    if (!questions.length) return { error: 'Add at least one question.' }
    for (const question of questions) {
      const options = question.options.split('\n').map((line) => line.trim()).filter(Boolean)
      if (options.length < 2) return { error: `Question "${question.text.trim()}" needs at least two options.` }
      const correctIndex = Number(question.correctIndex)
      if (!Number.isInteger(correctIndex) || correctIndex < 0 || correctIndex >= options.length) {
        return { error: `Question "${question.text.trim()}" needs a valid correct-answer index.` }
      }
    }
  }

  let activity: Record<string, unknown> = {}
  let answerKey: Record<string, unknown> = {}
  if (form.activityType === 'multiple_choice') {
    const questions = form.questions.filter((question) => question.text.trim())
    activity = {
      questions: questions.map((question, index) => ({
        id: `q${index + 1}`,
        text: question.text.trim(),
        options: question.options.split('\n').map((line) => line.trim()).filter(Boolean),
      })),
    }
    answerKey = {
      answers: Object.fromEntries(questions.map((question, index) => [`q${index + 1}`, Number(question.correctIndex)])),
    }
  } else {
    if (form.activityJson.trim()) {
      try {
        activity = JSON.parse(form.activityJson)
      } catch {
        return { error: 'Activity content must be valid JSON.' }
      }
    } else if (form.activityType === 'checklist') {
      const items = lines(form.checklistItems)
      if (!items.length) return { error: 'Add at least one checklist item.' }
      activity = { items }
      answerKey = { completedItemsRequired: items.length }
    } else if (form.activityType === 'short_answer') {
      const checklist = lines(form.checklistItems)
      if (!form.prompt.trim()) return { error: 'Short answer needs a prompt.' }
      if (!form.shortAnswerModelAnswer.trim()) return { error: 'Short answer needs a model answer.' }
      activity = {
        writingPrompt: form.prompt.trim(),
        checklist,
        minSentences: form.shortAnswerMinSentences ? Number(form.shortAnswerMinSentences) : 4,
      }
      answerKey = { modelAnswer: form.shortAnswerModelAnswer.trim() }
    } else if (form.activityType === 'reflection') {
      const guidingQuestions = lines(form.reflectionQuestions)
      if (!form.prompt.trim() && !guidingQuestions.length) return { error: 'Reflection needs a prompt or at least one guiding question.' }
      activity = { reflectionPrompt: form.prompt.trim(), guidingQuestions }
      answerKey = form.shortAnswerModelAnswer.trim() ? { sampleResponse: form.shortAnswerModelAnswer.trim() } : {}
    } else if (form.activityType === 'error_spotting') {
      const targetErrors = parseErrorTargets(form.errorSpottingTargets)
      if (!form.errorSpottingText.trim()) return { error: 'Error spotting needs a learner-facing text.' }
      if (!targetErrors.length) return { error: 'Add at least one target error and correction.' }
      activity = { text: form.errorSpottingText.trim(), selectableMode: 'word_or_phrase', targetErrorCount: targetErrors.length, targetErrors }
      answerKey = { targetErrors }
    } else if (form.activityType === 'ordering') {
      const items = lines(form.orderingItems)
      if (items.length < 2) return { error: 'Ordering needs at least two ordered items.' }
      activity = { items }
      answerKey = { correctOrder: items }
    } else if (form.activityType === 'matching') {
      const pairs = activeMatchPairs(form.matchingPairs)
      if (pairs.length < 2) return { error: 'Matching needs at least two complete pairs.' }
      const incomplete = pairs.find((pair) => !pair.left || !pair.right)
      if (incomplete) return { error: 'Each matching pair needs both sides.' }
      activity = { pairs }
      answerKey = { pairs }
    } else if (form.activityType === 'pronunciation_prompt') {
      const phrases = lines(form.pronunciationPhrases)
      if (!phrases.length) return { error: 'Pronunciation practice needs at least one phrase.' }
      activity = { phrases }
      answerKey = { practiceTargets: phrases }
    }
    if (form.answerKeyJson.trim()) {
      try {
        answerKey = { ...answerKey, ...JSON.parse(form.answerKeyJson) }
      } catch {
        return { error: 'Answer key must be valid JSON.' }
      }
    }
  }

  const lessonActivity = {
    lessonTitle: title,
    skill: form.skill.trim(),
    difficulty: form.difficulty.trim() || undefined,
    estimatedMinutes: form.estimatedMinutes ? Number(form.estimatedMinutes) : undefined,
    activityType: form.activityType,
    prompt: form.prompt.trim(),
    instructions: form.instructions.trim(),
    activity,
    answerKey,
    scoringRubric: {},
    feedbackRules: {},
    nextRecommendedActivity: '',
  }

  const concepts = form.tags.split(',').map((tag) => tag.trim()).filter(Boolean)

  return {
    payload: {
      templateKind: form.templateKind,
      title,
      skill: form.skill.trim(),
      competency: form.competency.trim() || null,
      concepts,
      difficulty: form.difficulty.trim() || null,
      language: form.language.trim() || 'en',
      estimatedMinutes: form.estimatedMinutes ? Number(form.estimatedMinutes) : null,
      activityType: form.activityType,
      lessonActivity,
      answerKey,
      coachNotes: form.description.trim() || null,
      status: form.status,
    },
  }
}

// Mentor/Admin surface for the reusable Learning Content Library. Both AI coaches and
// human mentors reuse the same lesson/quiz/activity/assessment templates here instead of
// regenerating content every session.
export const ContentLibraryPanel: React.FC = () => {
  const { t } = useI18n()
  const [tab, setTab] = useState<Tab>('templates')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)

  const [templates, setTemplates] = useState<LearningTemplate[]>([])
  const [reviewTemplates, setReviewTemplates] = useState<LearningTemplate[]>([])
  const [assignments, setAssignments] = useState<TemplateAssignment[]>([])
  const [filters, setFilters] = useState<TemplateListFilters>({})
  const [selected, setSelected] = useState<LearningTemplate | null>(null)
  const [selectedAttempts, setSelectedAttempts] = useState<CoachVisibleAttempt[]>([])

  // Find-or-generate form state.
  const [genTemplateKind, setGenTemplateKind] = useState<TemplateFormState['templateKind']>('quiz')
  const [genSkill, setGenSkill] = useState('')
  const [genCompetency, setGenCompetency] = useState('')
  const [genConcepts, setGenConcepts] = useState('')
  const [genDifficulty, setGenDifficulty] = useState('')
  const [genLanguage, setGenLanguage] = useState('en')
  const [genActivityType, setGenActivityType] = useState('multiple_choice')
  const [genTopic, setGenTopic] = useState('')
  const [genLearnerUserId, setGenLearnerUserId] = useState('')
  const [genForceVariation, setGenForceVariation] = useState(false)
  // Generation review state — what the admin sees after clicking "Find or generate".
  // Stays open until the admin explicitly dismisses it; never auto-closes on its own.
  const [genReviewOpen, setGenReviewOpen] = useState(false)
  const [genReview, setGenReview] = useState<FindOrGenerateResult | null>(null)
  const [genReviewError, setGenReviewError] = useState<string | null>(null)
  const [genRetrySaving, setGenRetrySaving] = useState(false)

  // Manual template authoring form state. The same form/modal is reused for three
  // flows: "New template" (create), "Edit manually from this output" (create, prefilled
  // from a just-generated lessonActivity), and "Edit content" on an existing saved
  // template (editingTemplateId set — submits via customize, which forks a new version
  // instead of mutating the original row, so past learner attempts stay pinned to the
  // version they actually attempted).
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [templateForm, setTemplateForm] = useState<TemplateFormState>(emptyTemplateForm())
  const [creatingTemplate, setCreatingTemplate] = useState(false)
  const [createError, setCreateError] = useState<string | null>(null)
  const [editingTemplateId, setEditingTemplateId] = useState<string | null>(null)

  // Manual assignment form state.
  const [showAssignModal, setShowAssignModal] = useState(false)
  const [activeTemplates, setActiveTemplates] = useState<LearningTemplate[]>([])
  const [learners, setLearners] = useState<MentorAdminRecord[]>([])
  const [assignLoading, setAssignLoading] = useState(false)
  const [assignError, setAssignError] = useState<string | null>(null)
  const [assignTemplateId, setAssignTemplateId] = useState('')
  const [assignLearnerIds, setAssignLearnerIds] = useState<string[]>([])
  const [assignDueDate, setAssignDueDate] = useState('')
  const [assignNote, setAssignNote] = useState('')
  const [assigning, setAssigning] = useState(false)
  const [assignResult, setAssignResult] = useState<string | null>(null)

  const loadTemplates = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await learningContentService.listTemplates(filters)
      setTemplates(response.data.templates || [])
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || t('Unable to load content library.'))
    } finally {
      setLoading(false)
    }
  }, [filters, t])

  const loadAssignments = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await learningContentService.listAssignments()
      setAssignments(response.data.assignments || [])
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || t('Unable to load assignments.'))
    } finally {
      setLoading(false)
    }
  }, [t])

  const loadReviewQueue = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await learningContentService.listReviewQueue()
      setReviewTemplates(response.data.templates || [])
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || t('Unable to load review queue.'))
    } finally {
      setLoading(false)
    }
  }, [t])

  useEffect(() => {
    if (tab === 'templates') void loadTemplates()
    if (tab === 'review') void loadReviewQueue()
    if (tab === 'assignments') void loadAssignments()
  }, [tab, loadTemplates, loadReviewQueue, loadAssignments])

  useEffect(() => {
    if (tab !== 'generate' || learners.length) return
    let cancelled = false
    mentorAdminService.listLearners()
      .then((response) => {
        if (!cancelled) setLearners(response.data.learners || [])
      })
      .catch(() => {
        // Learner targeting is helpful but optional; generation still works without it.
      })
    return () => {
      cancelled = true
    }
  }, [tab, learners.length])

  const setStatus = async (template: LearningTemplate, status: 'active' | 'archived' | 'draft') => {
    setBusyId(template.id)
    try {
      await learningContentService.updateTemplate(template.id, { status })
      await loadTemplates()
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || t('Unable to update template.'))
    } finally {
      setBusyId(null)
    }
  }

  const duplicate = async (template: LearningTemplate) => {
    setBusyId(template.id)
    try {
      await learningContentService.customizeTemplate(template.id, { title: `${template.title} (custom)`, ownerScope: 'coach' })
      await loadTemplates()
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || t('Unable to customize template.'))
    } finally {
      setBusyId(null)
    }
  }

  const archiveTemplateFromReview = async (template: LearningTemplate) => {
    setBusyId(template.id)
    setGenReviewError(null)
    try {
      const response = await learningContentService.updateTemplate(template.id, { status: 'archived' })
      setGenReview((prev) => (prev ? { ...prev, template: response.data.template } : prev))
      await Promise.all([loadTemplates(), tab === 'review' ? loadReviewQueue() : Promise.resolve()])
    } catch (err: any) {
      setGenReviewError(err?.response?.data?.message || err?.message || t('Unable to archive template.'))
    } finally {
      setBusyId(null)
    }
  }

  const reviewAction = async (template: LearningTemplate, action: 'reviewed' | 'approve' | 'archive') => {
    setBusyId(template.id)
    try {
      if (action === 'reviewed') await learningContentService.markReviewed(template.id)
      if (action === 'approve') await learningContentService.approveTemplate(template.id)
      if (action === 'archive') await learningContentService.archiveTemplate(template.id)
      await Promise.all([loadTemplates(), loadReviewQueue()])
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || t('Unable to update review status.'))
    } finally {
      setBusyId(null)
    }
  }

  const openTemplate = async (template: LearningTemplate) => {
    setSelected(template)
    setSelectedAttempts([])
    try {
      const response = await learningContentService.getTemplate(template.id)
      setSelectedAttempts(response.data.attempts || [])
    } catch {
      // Detail attempts are best-effort.
    }
  }

  const openCreateModal = () => {
    setTemplateForm(emptyTemplateForm())
    setEditingTemplateId(null)
    setCreateError(null)
    setShowCreateModal(true)
  }

  // "Edit content" on an existing saved template (from the Templates or Review Queue
  // tab). Skill/language/activity type/estimated minutes are shown but disabled in the
  // form because /customize (see submitCreateTemplate) only overrides content fields —
  // changing those would silently not persist, so the form doesn't offer to.
  const openEditModal = (template: LearningTemplate) => {
    setTemplateForm(templateFormFromTemplate(template))
    setEditingTemplateId(template.id)
    setCreateError(null)
    setShowCreateModal(true)
  }

  const updateQuestion = (index: number, patch: Partial<ManualQuestionDraft>) => {
    setTemplateForm((prev) => ({
      ...prev,
      questions: prev.questions.map((question, questionIndex) => (questionIndex === index ? { ...question, ...patch } : question)),
    }))
  }

  const updateMatchingPair = (index: number, patch: Partial<MatchPairDraft>) => {
    setTemplateForm((prev) => ({
      ...prev,
      matchingPairs: prev.matchingPairs.map((pair, pairIndex) => (pairIndex === index ? { ...pair, ...patch } : pair)),
    }))
  }

  const submitCreateTemplate = async () => {
    const result = buildTemplatePayload(templateForm)
    if ('error' in result) {
      setCreateError(result.error)
      return
    }
    setCreatingTemplate(true)
    setCreateError(null)
    try {
      if (editingTemplateId) {
        // Fork a new version with the edited content rather than mutating the existing
        // row in place, so any past learner attempts stay pinned to the version they
        // actually attempted (see buildCustomizedTemplateRecord in
        // learningContentService.ts). The new version supersedes the one being edited.
        await learningContentService.customizeTemplate(editingTemplateId, {
          title: result.payload.title,
          difficulty: result.payload.difficulty,
          concepts: result.payload.concepts,
          coachNotes: result.payload.coachNotes,
          lessonActivity: result.payload.lessonActivity,
          answerKey: result.payload.answerKey,
          status: result.payload.status,
        })
      } else {
        await learningContentService.createTemplate(result.payload)
      }
      setShowCreateModal(false)
      setEditingTemplateId(null)
      await Promise.all([loadTemplates(), tab === 'review' ? loadReviewQueue() : Promise.resolve()])
    } catch (err: any) {
      setCreateError(err?.response?.data?.message || err?.message || t(editingTemplateId ? 'Unable to save the edited template.' : 'Unable to create template.'))
    } finally {
      setCreatingTemplate(false)
    }
  }

  const openAssignModal = async () => {
    setAssignTemplateId('')
    setAssignLearnerIds([])
    setAssignDueDate('')
    setAssignNote('')
    setAssignResult(null)
    setAssignError(null)
    setShowAssignModal(true)
    setAssignLoading(true)
    try {
      const [templatesResponse, learnersResponse] = await Promise.all([
        learningContentService.listTemplates({ status: 'active' }),
        mentorAdminService.listLearners(),
      ])
      setActiveTemplates(templatesResponse.data.templates || [])
      setLearners(learnersResponse.data.learners || [])
    } catch (err: any) {
      setAssignError(err?.response?.data?.message || err?.message || t('Unable to load templates or learners.'))
    } finally {
      setAssignLoading(false)
    }
  }

  const toggleAssignLearner = (learnerUserId: string) => {
    setAssignLearnerIds((prev) => (prev.includes(learnerUserId) ? prev.filter((id) => id !== learnerUserId) : [...prev, learnerUserId]))
  }

  const submitAssignment = async () => {
    if (!assignTemplateId || !assignLearnerIds.length) {
      setAssignError(t('Choose a template and at least one learner.'))
      return
    }
    setAssigning(true)
    setAssignError(null)
    setAssignResult(null)
    let succeeded = 0
    let failed = 0
    for (const learnerUserId of assignLearnerIds) {
      try {
        await learningContentService.createAssignment({
          templateId: assignTemplateId,
          learnerUserId,
          assignedByType: 'human',
          dueDate: assignDueDate || null,
          personalizedNote: assignNote.trim() || null,
        })
        succeeded += 1
      } catch {
        failed += 1
      }
    }
    setAssigning(false)
    setAssignResult(failed ? `${t('Assigned to')} ${succeeded}, ${failed} ${t('failed')}.` : `${t('Assigned to')} ${succeeded} ${succeeded === 1 ? t('learner') : t('learners')}.`)
    if (succeeded) {
      setAssignLearnerIds([])
      await loadAssignments()
    }
  }

  // Generate (or force a fresh variation on retry). Always opens the review panel with
  // whatever came back — including a synthesized "malformed" review when the request
  // itself fails — so the admin always sees what happened and never has to guess whether
  // anything was saved.
  const runFindOrGenerate = async (options: { forceVariation?: boolean } = {}) => {
    if (!genSkill.trim()) return
    setBusyId('generate')
    setGenReviewError(null)
    try {
      const response = await learningContentService.findOrGenerate({
        templateKind: genTemplateKind,
        skill: genSkill.trim(),
        competency: genCompetency.trim() || null,
        concepts: genConcepts.split(',').map((concept) => concept.trim()).filter(Boolean),
        difficulty: genDifficulty.trim() || null,
        language: genLanguage.trim() || 'en',
        activityType: genActivityType,
        learnerUserId: genLearnerUserId || null,
        promptContext: genTopic.trim() || null,
        forceVariation: options.forceVariation ?? genForceVariation,
      })
      setGenReview(response.data)
      setGenReviewOpen(true)
      if (response.data.template) await loadTemplates()
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.response?.data?.error?.message || err?.message || t('Unable to find or generate content.')
      setGenReview({
        success: false,
        source: 'generated',
        reuseDecision: 'generated_new',
        reason: null,
        fallback: true,
        fallbackReason: null,
        validationStatus: 'malformed',
        lessonActivity: undefined,
        template: null,
        aiInteractionId: null,
        saveStatus: 'failed',
        saveError: message,
      })
      setGenReviewOpen(true)
    } finally {
      setBusyId(null)
    }
  }

  // Retry persisting the reviewed lessonActivity after a save failure, without
  // re-running generation (the admin already reviewed this content).
  const retrySaveGeneratedTemplate = async () => {
    if (!genReview?.lessonActivity) return
    const lessonActivity = genReview.lessonActivity
    const activityType = (lessonActivityField(lessonActivity, 'activityType') as LessonActivityType) || 'checklist'
    setGenRetrySaving(true)
    try {
      const response = await learningContentService.createTemplate({
        templateKind: ACTIVITY_TYPE_TO_KIND[activityType] || 'lesson',
        title: asText(lessonActivityField(lessonActivity, 'lessonTitle')) || genSkill.trim(),
        skill: asText(lessonActivityField(lessonActivity, 'skill')) || genSkill.trim(),
        difficulty: asText(lessonActivityField(lessonActivity, 'difficulty')) || null,
        activityType,
        lessonActivity,
        answerKey: (lessonActivityField(lessonActivity, 'answerKey') as Record<string, unknown>) || {},
        status: 'draft',
      })
      const template = response.data.template
      setGenReview((prev) => (prev ? { ...prev, template, saveStatus: template.status === 'active' ? 'saved_active' : 'saved_draft', saveError: null } : prev))
      await loadTemplates()
      setSelected(template)
    } catch (err: any) {
      setGenReviewError(err?.response?.data?.message || err?.message || t('Unable to save the generated template.'))
    } finally {
      setGenRetrySaving(false)
    }
  }

  // "Edit manually from this output" — reuses the existing manual authoring form/modal
  // and contract instead of inventing a second editor for generated content. If
  // generation already saved a draft template (the default), edit that saved row via
  // customize instead of creating a second, orphaned template with the same content.
  const editGeneratedManually = () => {
    if (!genReview?.lessonActivity) return
    setTemplateForm(templateFormFromLessonActivity(genReview.lessonActivity))
    setEditingTemplateId(genReview.template?.id ?? null)
    setCreateError(null)
    setGenReviewOpen(false)
    setShowCreateModal(true)
  }

  const closeGenReview = () => {
    setGenReviewOpen(false)
  }

  const openGeneratedTemplate = async (template: LearningTemplate) => {
    setGenReviewOpen(false)
    await openTemplate(template)
  }

  const tabs: Array<{ id: Tab; label: string }> = useMemo(() => ([
    { id: 'templates', label: t('Templates') },
    { id: 'review', label: t('Review Queue') },
    { id: 'assignments', label: t('Assignments') },
    { id: 'generate', label: t('Reuse / Generate') },
  ]), [t])

  return (
    <section className="stack">
      <div className="admin-panel-toolbar">
        <div>
          <h3 style={{ margin: 0 }}>{t('Learning Content Library')}</h3>
          <p className="helper">{t('Reusable lesson plans, quizzes, activities, and assessments shared by AI and human coaches. Learner attempts never change a template.')}</p>
          <p className="helper">{t('Start in "Reuse / Generate" to create content for a skill, review it in "Review Queue" before it goes live, then browse and manage it here in "Templates". You can also author a template by hand below, or assign an active template to one or more learners directly.')}</p>
        </div>
        <div className="button-row">
          <button type="button" className="btn btn-primary" onClick={openCreateModal}>{t('New template')}</button>
          <button type="button" className="btn btn-ghost" onClick={openAssignModal}>{t('New assignment')}</button>
        </div>
      </div>

      <div className="button-row">
        {tabs.map((item) => (
          <button
            key={item.id}
            type="button"
            className={`btn ${tab === item.id ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => setTab(item.id)}
          >
            {item.label}
          </button>
        ))}
      </div>

      {error ? <ErrorBanner message={error} /> : null}
      {loading ? <LoadingState label={t('Loading...')} /> : null}

      {tab === 'templates' && !loading ? (
        <>
          <div className="button-row">
            <select value={filters.templateKind || ''} onChange={(event) => setFilters((prev) => ({ ...prev, templateKind: (event.target.value || undefined) as TemplateListFilters['templateKind'] }))}>
              <option value="">{t('All kinds')}</option>
              {['lesson', 'quiz', 'activity', 'assessment'].map((kind) => <option key={kind} value={kind}>{titleCase(kind)}</option>)}
            </select>
            <select value={filters.status || ''} onChange={(event) => setFilters((prev) => ({ ...prev, status: (event.target.value || undefined) as TemplateListFilters['status'] }))}>
              <option value="">{t('All statuses')}</option>
              {['active', 'draft', 'archived'].map((status) => <option key={status} value={status}>{titleCase(status)}</option>)}
            </select>
            <input
              type="text"
              placeholder={t('Search by skill')}
              value={filters.skill || ''}
              onChange={(event) => setFilters((prev) => ({ ...prev, skill: event.target.value || undefined }))}
            />
          </div>
          {templates.length ? (
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    {['Title', 'Kind', 'Skill', 'Difficulty', 'Version', 'Status', 'Created by', 'Reuse', 'Actions'].map((column) => <th key={column}>{t(column)}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {templates.map((template) => (
                    <tr key={template.id}>
                      <td><strong>{template.title}</strong></td>
                      <td>{titleCase(template.templateKind)}</td>
                      <td>{template.skill || '-'}</td>
                      <td>{template.difficulty || '-'}</td>
                      <td>v{template.version}</td>
                      <td><span className={statusClass(template.status)}>{t(titleCase(template.status))}</span></td>
                      <td><span className="status-pill status-pill--muted">{titleCase(template.createdByType)}</span></td>
                      <td>{template.reuseCount}</td>
                      <td>
                        <EntityActionMenu label={t('Actions')}>
                          <button type="button" onClick={() => openTemplate(template)}>{t('View details')}</button>
                          <button type="button" onClick={() => openEditModal(template)}>{t('Edit content')}</button>
                          <button type="button" onClick={() => duplicate(template)} disabled={busyId === template.id}>{t('Duplicate / customize')}</button>
                          <button type="button" onClick={() => setStatus(template, 'active')} disabled={busyId === template.id || template.status === 'active'}>{t('Activate')}</button>
                          <button type="button" onClick={() => setStatus(template, 'archived')} disabled={busyId === template.id || template.status === 'archived'}>{t('Delete / archive')}</button>
                        </EntityActionMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <EmptyState title={t('No templates yet')} description={t('Use the "Reuse / Generate" tab to create your first reusable lesson, quiz, activity, or assessment for a skill.')} />
          )}
        </>
      ) : null}

      {tab === 'review' && !loading ? (
        reviewTemplates.length ? (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  {['Title', 'Status', 'Quality', 'Reports', 'Ratings', 'Fallback', 'Last reviewed', 'Actions'].map((column) => <th key={column}>{t(column)}</th>)}
                </tr>
              </thead>
              <tbody>
                {reviewTemplates.map((template) => (
                  <tr key={template.id}>
                    <td>
                      <strong>{template.title}</strong>
                      <p className="helper">{template.skill || '-'} · {template.difficulty || '-'} · v{template.version}</p>
                    </td>
                    <td><span className={statusClass(template.status)}>{t(titleCase(template.status))}</span></td>
                    <td>{template.qualityScore ?? '-'}</td>
                    <td>
                      {template.reportCount ?? 0}
                      {(template.unresolvedIncorrectAnswerReportCount ?? 0) > 0 ? (
                        <p className="helper">{t('Incorrect answer reports')}: {template.unresolvedIncorrectAnswerReportCount}</p>
                      ) : null}
                    </td>
                    <td>
                      {template.learnerRatingAverage ? `${template.learnerRatingAverage}/5` : '-'}
                      {template.coachRatingAverage ? <p className="helper">{t('Coach')}: {template.coachRatingAverage}/5</p> : null}
                    </td>
                    <td>
                      {template.fallbackGenerated || (template.malformedFallbackCount ?? 0) > 0 ? t('Needs review') : '-'}
                      {(template.malformedFallbackCount ?? 0) > 0 ? <p className="helper">{t('Malformed fallback')}: {template.malformedFallbackCount}</p> : null}
                      {template.fallbackReason ? (
                        <p className="helper" data-testid="template-fallback-reason">
                          {t(FALLBACK_REASON_LABEL[template.fallbackReason] || titleCase(template.fallbackReason))}
                        </p>
                      ) : null}
                    </td>
                    <td>{template.lastReviewedAt ? new Date(template.lastReviewedAt).toLocaleDateString() : t('Never')}</td>
                    <td>
                      <EntityActionMenu label={t('Actions')}>
                        <button type="button" onClick={() => openTemplate(template)}>{t('View details')}</button>
                        <button type="button" onClick={() => openEditModal(template)}>{t('Edit content')}</button>
                        <button type="button" onClick={() => reviewAction(template, 'reviewed')} disabled={busyId === template.id}>{t('Mark reviewed')}</button>
                        <button type="button" onClick={() => reviewAction(template, 'approve')} disabled={busyId === template.id}>{t('Approve')}</button>
                        <button type="button" onClick={() => reviewAction(template, 'archive')} disabled={busyId === template.id}>{t('Archive')}</button>
                      </EntityActionMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState title={t('No templates need review')} description={t('Quality checks, reports, and stale-review rules are clear right now.')} />
        )
      ) : null}

      {tab === 'assignments' && !loading ? (
        assignments.length ? (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  {['Content', 'Skill', 'Assigned by', 'Status', 'Completion', 'Due'].map((column) => <th key={column}>{t(column)}</th>)}
                </tr>
              </thead>
              <tbody>
                {assignments.map((assignment) => (
                  <tr key={assignment.id}>
                    <td><strong>{assignment.templateTitle || assignment.templateId}</strong><p className="helper">{titleCase(assignment.templateKind)}</p></td>
                    <td>{assignment.templateSkill || '-'}</td>
                    <td><span className="status-pill status-pill--muted">{titleCase(assignment.assignedByType)}</span></td>
                    <td><span className={statusClass(assignment.status === 'completed' ? 'active' : assignment.status === 'cancelled' ? 'archived' : 'draft')}>{titleCase(assignment.status)}</span></td>
                    <td>{titleCase(assignment.completionStatus)}</td>
                    <td>{assignment.dueDate ? new Date(assignment.dueDate).toLocaleDateString() : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState
            title={t('No assignments yet')}
            description={t('Assignments appear here once an AI coach or mentor matches a learner to a template, or you assign an active template manually with "New assignment" above.')}
            action={<button type="button" className="btn btn-primary" onClick={openAssignModal}>{t('New assignment')}</button>}
          />
        )
      ) : null}

      {tab === 'generate' ? (
        <section className="stack" style={{ maxWidth: 720 }}>
          <p className="helper">{t('The AI coach searches reusable templates first and only generates when no suitable template exists, the difficulty differs, the version is outdated, quality is too low, or you request a variation.')}</p>
          <p className="helper">{t('Use a specific skill plus topic, competency, concepts, language, and learner when available. Those fields are sent to reuse matching and generation so quizzes/exercises are less generic and rotate away from what a learner recently saw.')}</p>
          <div className="admin-form-grid">
            <label>{t('Content kind')}
              <select className="input" value={genTemplateKind} onChange={(event) => setGenTemplateKind(event.target.value as TemplateFormState['templateKind'])}>
                {['lesson', 'quiz', 'activity', 'assessment'].map((kind) => <option key={kind} value={kind}>{titleCase(kind)}</option>)}
              </select>
            </label>
            <label>{t('Activity type')}
              <select className="input" value={genActivityType} onChange={(event) => setGenActivityType(event.target.value)}>
                {['multiple_choice', 'error_spotting', 'short_answer', 'ordering', 'matching', 'pronunciation_prompt', 'checklist', 'reflection'].map((type) => (
                  <option key={type} value={type}>{titleCase(type)}</option>
                ))}
              </select>
            </label>
            <label>{t('Skill')}
              <input className="input" type="text" value={genSkill} onChange={(event) => setGenSkill(event.target.value)} placeholder={t('e.g. French reading comprehension')} />
            </label>
            <label>{t('Difficulty')}
              <input className="input" type="text" value={genDifficulty} onChange={(event) => setGenDifficulty(event.target.value)} placeholder={t('e.g. secondary_2')} />
            </label>
            <label>{t('Language')}
              <input className="input" type="text" value={genLanguage} onChange={(event) => setGenLanguage(event.target.value)} placeholder={t('e.g. fr-CA')} />
            </label>
            <label>{t('Competency')}
              <input className="input" type="text" value={genCompetency} onChange={(event) => setGenCompetency(event.target.value)} placeholder={t('e.g. infer meaning from a short passage')} />
            </label>
            <label className="admin-form-wide">{t('Topic / scenario')}
              <textarea className="input" rows={3} value={genTopic} onChange={(event) => setGenTopic(event.target.value)} placeholder={t('e.g. A Secondary 2 student reads a short text about a weekend hockey practice and answers inference questions.')} />
            </label>
            <label className="admin-form-wide">{t('Concepts / skillset focus (comma separated)')}
              <input className="input" value={genConcepts} onChange={(event) => setGenConcepts(event.target.value)} placeholder={t('e.g. vocabulary in context, inference, passé composé')} />
            </label>
            <label className="admin-form-wide">{t('Learner-aware rotation (optional)')}
              <select className="input" value={genLearnerUserId} onChange={(event) => setGenLearnerUserId(event.target.value)}>
                <option value="">{t('No specific learner')}</option>
                {learners.map((learner) => {
                  const learnerUserId = String(learner.learnerUserId || '')
                  if (!learnerUserId) return null
                  return (
                    <option key={String(learner.id)} value={learnerUserId}>
                      {String(learner.workerName || learner.learnerName || t('Learner'))} — {String(learner.skillName || '-')}
                    </option>
                  )
                })}
              </select>
            </label>
          </div>
          <label className="button-row" style={{ alignItems: 'center' }}>
            <input type="checkbox" checked={genForceVariation} onChange={(event) => setGenForceVariation(event.target.checked)} />
            <span>{t('Force a fresh variation (skip reuse)')}</span>
          </label>
          <div className="button-row">
            <button type="button" className="btn btn-primary" onClick={() => runFindOrGenerate()} disabled={busyId === 'generate' || !genSkill.trim()}>
              {busyId === 'generate' ? t('Working...') : t('Find or generate')}
            </button>
          </div>
        </section>
      ) : null}

      {genReviewOpen && genReview ? (
        <DetailDrawer
          title={genReview.source === 'reused' ? t('Reused existing template') : t('Generated content review')}
          subtitle={genReview.reason ? titleCase(genReview.reason) : undefined}
          isOpen
          onClose={closeGenReview}
          actions={(
            <div className="button-row">
              <button type="button" className="btn btn-ghost" onClick={() => runFindOrGenerate({ forceVariation: true })} disabled={busyId === 'generate'}>
                {busyId === 'generate' ? t('Working...') : t('Try again')}
              </button>
              {genReview.lessonActivity ? (
                <button type="button" className="btn btn-ghost" onClick={editGeneratedManually}>{t('Edit manually from this output')}</button>
              ) : null}
              {genReview.saveStatus === 'failed' && genReview.lessonActivity ? (
                <button type="button" className="btn btn-primary" onClick={retrySaveGeneratedTemplate} disabled={genRetrySaving}>
                  {genRetrySaving ? t('Saving...') : t('Save template')}
                </button>
              ) : null}
              {genReview.template && genReview.template.status !== 'archived' ? (
                <button type="button" className="btn btn-ghost" onClick={() => archiveTemplateFromReview(genReview.template as LearningTemplate)} disabled={busyId === (genReview.template as LearningTemplate).id}>{t('Delete / archive saved template')}</button>
              ) : null}
              {genReview.template ? (
                <button type="button" className="btn btn-primary" onClick={() => openGeneratedTemplate(genReview.template as LearningTemplate)}>{t('Open template')}</button>
              ) : null}
            </div>
          )}
        >
          <div className="stack" data-testid="generation-review">
            <ErrorBanner message={genReviewError || undefined} />
            <div className="button-row" style={{ alignItems: 'center' }}>
              {genReview.reuseDecision ? (
                <span className={statusClass(genReview.reuseDecision === 'duplicate_rejected' ? 'draft' : genReview.source === 'reused' ? 'active' : 'draft')} data-testid="generation-reuse-decision">
                  {t(REUSE_DECISION_LABEL[genReview.reuseDecision] || titleCase(genReview.reuseDecision))}
                </span>
              ) : null}
              <span className={statusClass(genReview.validationStatus === 'valid' ? 'active' : genReview.validationStatus === 'malformed' ? 'archived' : 'draft')} data-testid="generation-validation-status">
                {t(VALIDATION_STATUS_LABEL[genReview.validationStatus || 'valid'] || titleCase(genReview.validationStatus))}
              </span>
              <span className={statusClass(genReview.saveStatus === 'saved_active' ? 'active' : genReview.saveStatus === 'failed' ? 'archived' : 'draft')} data-testid="generation-save-status">
                {t(SAVE_STATUS_LABEL[genReview.saveStatus || 'not_saved'] || titleCase(genReview.saveStatus))}
              </span>
            </div>
            {genReview.reuseDecision === 'duplicate_rejected' ? (
              <p className="helper" data-testid="generation-duplicate-message">
                {t('A similar high-quality lesson already exists, so this learner was assigned that version instead.')}
              </p>
            ) : null}
            {genReview.diversity ? (
              <p className="helper" data-testid="generation-diversity">
                {t('Theme')}: {genReview.diversity.theme} · {t('Focus')}: {genReview.diversity.focus}
              </p>
            ) : null}
            <p className="helper">{t(VALIDATION_STATUS_HELP[genReview.validationStatus || 'valid'] || '')}</p>
            {genReview.fallbackReason ? (
              <div data-testid="generation-fallback-reason">
                <strong>{t('Why this fell back')}: {t(FALLBACK_REASON_LABEL[genReview.fallbackReason] || titleCase(genReview.fallbackReason))}</strong>
                <p className="helper">{t(FALLBACK_REASON_HELP[genReview.fallbackReason] || '')}</p>
              </div>
            ) : null}
            {genReview.saveStatus === 'failed' && genReview.saveError ? <ErrorBanner message={genReview.saveError} testId="generation-save-error" /> : null}

            {genReview.lessonActivity ? (
              <>
                <h4 style={{ marginBottom: 0 }}>{asText(lessonActivityField(genReview.lessonActivity, 'lessonTitle')) || t('Untitled')}</h4>
                <p className="helper">
                  {titleCase(String(lessonActivityField(genReview.lessonActivity, 'activityType') || '-'))}
                  {' · '}{asText(lessonActivityField(genReview.lessonActivity, 'skill')) || '-'}
                  {' · '}{asText(lessonActivityField(genReview.lessonActivity, 'difficulty')) || '-'}
                  {' · '}{asText(lessonActivityField(genReview.lessonActivity, 'estimatedMinutes')) || '-'} {t('min')}
                  {' · '}{genReview.template?.language || '-'}
                </p>
                <div>
                  <strong>{t('Prompt')}</strong>
                  <p>{asText(lessonActivityField(genReview.lessonActivity, 'prompt')) || '-'}</p>
                </div>
                <div>
                  <strong>{t('Instructions')}</strong>
                  <p>{asText(lessonActivityField(genReview.lessonActivity, 'instructions')) || '-'}</p>
                </div>
                {(() => {
                  const activityType = lessonActivityField(genReview.lessonActivity, 'activityType')
                  const activity = lessonActivityField(genReview.lessonActivity, 'activity') as Record<string, unknown> | undefined
                  if (activityType === 'multiple_choice' && Array.isArray(activity?.questions)) {
                    return (
                      <div>
                        <strong>{t('Questions')}</strong>
                        <ul>
                          {(activity?.questions as Array<Record<string, unknown>>).map((question, index) => (
                            <li key={String(question.id || index)}>
                              {asText(question.question)}
                              <ul>
                                {(Array.isArray(question.options) ? question.options : []).map((option: unknown, optionIndex: number) => (
                                  <li key={optionIndex} style={{ fontWeight: option === question.correctAnswer ? 700 : 400 }}>
                                    {asText(option)}{option === question.correctAnswer ? ` (${t('correct')})` : ''}
                                  </li>
                                ))}
                              </ul>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )
                  }
                  return (
                    <div>
                      <strong>{t('Activity content')}</strong>
                      <pre className="admin-code-block">{JSON.stringify(activity || {}, null, 2)}</pre>
                    </div>
                  )
                })()}
                <div>
                  <strong>{t('Answer key (admin only)')}</strong>
                  <pre className="admin-code-block">{JSON.stringify(lessonActivityField(genReview.lessonActivity, 'answerKey') || {}, null, 2)}</pre>
                </div>
              </>
            ) : (
              <EmptyState title={t('No generated content to show')} description={t('The request failed before any content was produced. Try again, or check the LLM provider configuration.')} />
            )}
          </div>
        </DetailDrawer>
      ) : null}

      {selected ? (
        <DetailDrawer title={selected.title} isOpen onClose={() => setSelected(null)}>
          <div className="stack">
            <p className="helper">{titleCase(selected.templateKind)} · {selected.skill || '-'} · {selected.difficulty || '-'} · v{selected.version}</p>
            {selected.coachNotes ? <p>{selected.coachNotes}</p> : null}
            <h4 style={{ marginBottom: 0 }}>{t('Learner attempts')}</h4>
            {selectedAttempts.length ? (
              <ul>
                {selectedAttempts.map((attempt) => (
                  <li key={attempt.id}>
                    {attempt.score ?? '-'}/{attempt.maxScore ?? '-'} · {titleCase(attempt.status)}
                    {attempt.feedbackSummary ? ` — ${attempt.feedbackSummary}` : ''}
                  </li>
                ))}
              </ul>
            ) : <p className="helper">{t('No attempts recorded for this template yet.')}</p>}
          </div>
        </DetailDrawer>
      ) : null}

      <FormModal
        title={editingTemplateId ? t('Edit content') : t('New template')}
        description={editingTemplateId
          ? t('Editing saves a new version with your changes and supersedes the current one — past learner attempts stay pinned to the version they actually completed. Skill, language, activity type, and estimated minutes can\'t be changed here; create a new template instead if those need to change. Set status to "Active" once you\'re done reviewing, or keep it as "Draft" to keep refining.')
          : t('Author a reusable lesson, quiz, activity, or assessment by hand using the same mobile lesson contract the AI coach uses. Set status to "Active" to make it assignable right away, or save as "Draft" to review first.')}
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        size="lg"
        footer={(
          <>
            <button type="button" className="btn btn-ghost" onClick={() => setShowCreateModal(false)} disabled={creatingTemplate}>{t('Cancel')}</button>
            <button type="button" className="btn btn-primary" onClick={submitCreateTemplate} disabled={creatingTemplate}>
              {creatingTemplate ? t('Saving...') : editingTemplateId ? t('Save new version') : t('Save template')}
            </button>
          </>
        )}
      >
        <ErrorBanner message={createError || undefined} />
        <div className="admin-form-grid">
          <label>{t('Title')}
            <input className="input" value={templateForm.title} onChange={(event) => setTemplateForm((prev) => ({ ...prev, title: event.target.value }))} placeholder={t('e.g. Ladder safety checks — Level 1')} />
          </label>
          <label>{t('Template kind')}
            <select className="input" value={templateForm.templateKind} onChange={(event) => setTemplateForm((prev) => ({ ...prev, templateKind: event.target.value as TemplateFormState['templateKind'] }))} disabled={Boolean(editingTemplateId)}>
              {['lesson', 'quiz', 'activity', 'assessment'].map((kind) => <option key={kind} value={kind}>{titleCase(kind)}</option>)}
            </select>
          </label>
          <label>{t('Skill')}
            <input className="input" value={templateForm.skill} onChange={(event) => setTemplateForm((prev) => ({ ...prev, skill: event.target.value }))} placeholder={t('e.g. ladder safety checks')} disabled={Boolean(editingTemplateId)} />
          </label>
          <label>{t('Competency')}
            <input className="input" value={templateForm.competency} onChange={(event) => setTemplateForm((prev) => ({ ...prev, competency: event.target.value }))} />
          </label>
          <label>{t('Difficulty')}
            <input className="input" value={templateForm.difficulty} onChange={(event) => setTemplateForm((prev) => ({ ...prev, difficulty: event.target.value }))} placeholder={t('e.g. beginner')} />
          </label>
          <label>{t('Language')}
            <input className="input" value={templateForm.language} onChange={(event) => setTemplateForm((prev) => ({ ...prev, language: event.target.value }))} disabled={Boolean(editingTemplateId)} />
          </label>
          <label>{t('Estimated minutes')}
            <input className="input" type="number" min={0} value={templateForm.estimatedMinutes} onChange={(event) => setTemplateForm((prev) => ({ ...prev, estimatedMinutes: event.target.value }))} disabled={Boolean(editingTemplateId)} />
          </label>
          <label>{t('Activity type')}
            <select className="input" value={templateForm.activityType} onChange={(event) => setTemplateForm((prev) => ({ ...prev, activityType: event.target.value as LessonActivityType }))} disabled={Boolean(editingTemplateId)}>
              {ACTIVITY_TYPES.map((type) => <option key={type} value={type}>{titleCase(type)}</option>)}
            </select>
          </label>
          <label>{t('Status')}
            <select className="input" value={templateForm.status} onChange={(event) => setTemplateForm((prev) => ({ ...prev, status: event.target.value as TemplateFormState['status'] }))}>
              <option value="draft">{t('Draft')}</option>
              <option value="active">{t('Active')}</option>
            </select>
          </label>
          <label className="admin-form-wide">{t('Description / coach notes')}
            <textarea className="input" rows={2} value={templateForm.description} onChange={(event) => setTemplateForm((prev) => ({ ...prev, description: event.target.value }))} />
          </label>
          <label className="admin-form-wide">{t('Prompt')}
            <textarea className="input" rows={3} value={templateForm.prompt} onChange={(event) => setTemplateForm((prev) => ({ ...prev, prompt: event.target.value }))} placeholder={t('What the learner sees as the lesson/question prompt.')} />
          </label>
          <label className="admin-form-wide">{t('Instructions')}
            <textarea className="input" rows={2} value={templateForm.instructions} onChange={(event) => setTemplateForm((prev) => ({ ...prev, instructions: event.target.value }))} />
          </label>
          <label className="admin-form-wide">{t('Tags / concepts (comma separated)')}
            <input className="input" value={templateForm.tags} onChange={(event) => setTemplateForm((prev) => ({ ...prev, tags: event.target.value }))} />
          </label>

          {templateForm.activityType === 'multiple_choice' ? (
            <div className="admin-form-wide stack">
              <p className="helper">{t('Add one or more questions. List each answer option on its own line, then give the correct option\'s position (0 = first line).')}</p>
              {templateForm.questions.map((question, index) => (
                <div key={index} className="stack" style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 12 }}>
                  <label>{t('Question')} {index + 1}
                    <input className="input" value={question.text} onChange={(event) => updateQuestion(index, { text: event.target.value })} />
                  </label>
                  <label>{t('Options (one per line)')}
                    <textarea className="input" rows={3} value={question.options} onChange={(event) => updateQuestion(index, { options: event.target.value })} />
                  </label>
                  <label>{t('Correct option index')}
                    <input className="input" type="number" min={0} value={question.correctIndex} onChange={(event) => updateQuestion(index, { correctIndex: event.target.value })} />
                  </label>
                  {templateForm.questions.length > 1 ? (
                    <button type="button" className="btn btn-ghost" onClick={() => setTemplateForm((prev) => ({ ...prev, questions: prev.questions.filter((_, questionIndex) => questionIndex !== index) }))}>
                      {t('Remove question')}
                    </button>
                  ) : null}
                </div>
              ))}
              <button type="button" className="btn btn-ghost" onClick={() => setTemplateForm((prev) => ({ ...prev, questions: [...prev.questions, emptyQuestion()] }))}>
                {t('Add question')}
              </button>
            </div>
          ) : (
            <>
              {templateForm.activityType === 'checklist' ? (
                <label className="admin-form-wide">{t('Checklist items (one per line)')}
                  <textarea className="input" rows={5} value={templateForm.checklistItems} onChange={(event) => setTemplateForm((prev) => ({ ...prev, checklistItems: event.target.value }))} />
                </label>
              ) : null}

              {templateForm.activityType === 'short_answer' ? (
                <>
                  <label>{t('Minimum sentences')}
                    <input className="input" type="number" min={1} value={templateForm.shortAnswerMinSentences} onChange={(event) => setTemplateForm((prev) => ({ ...prev, shortAnswerMinSentences: event.target.value }))} />
                  </label>
                  <label className="admin-form-wide">{t('Writing checklist (one per line)')}
                    <textarea className="input" rows={3} value={templateForm.checklistItems} onChange={(event) => setTemplateForm((prev) => ({ ...prev, checklistItems: event.target.value }))} />
                  </label>
                  <label className="admin-form-wide">{t('Model answer')}
                    <textarea className="input" rows={4} value={templateForm.shortAnswerModelAnswer} onChange={(event) => setTemplateForm((prev) => ({ ...prev, shortAnswerModelAnswer: event.target.value }))} />
                  </label>
                </>
              ) : null}

              {templateForm.activityType === 'reflection' ? (
                <>
                  <label className="admin-form-wide">{t('Guiding questions (one per line)')}
                    <textarea className="input" rows={4} value={templateForm.reflectionQuestions} onChange={(event) => setTemplateForm((prev) => ({ ...prev, reflectionQuestions: event.target.value }))} />
                  </label>
                  <label className="admin-form-wide">{t('Sample response (optional)')}
                    <textarea className="input" rows={3} value={templateForm.shortAnswerModelAnswer} onChange={(event) => setTemplateForm((prev) => ({ ...prev, shortAnswerModelAnswer: event.target.value }))} />
                  </label>
                </>
              ) : null}

              {templateForm.activityType === 'error_spotting' ? (
                <>
                  <label className="admin-form-wide">{t('Text with intentional errors')}
                    <textarea className="input" rows={4} value={templateForm.errorSpottingText} onChange={(event) => setTemplateForm((prev) => ({ ...prev, errorSpottingText: event.target.value }))} />
                  </label>
                  <label className="admin-form-wide">{t('Target errors (one per line: error => correction | explanation)')}
                    <textarea className="input" rows={4} value={templateForm.errorSpottingTargets} onChange={(event) => setTemplateForm((prev) => ({ ...prev, errorSpottingTargets: event.target.value }))} placeholder={t('manges => mange | Subject and verb must agree.')} />
                  </label>
                </>
              ) : null}

              {templateForm.activityType === 'ordering' ? (
                <label className="admin-form-wide">{t('Correct order (one step per line)')}
                  <textarea className="input" rows={5} value={templateForm.orderingItems} onChange={(event) => setTemplateForm((prev) => ({ ...prev, orderingItems: event.target.value }))} />
                </label>
              ) : null}

              {templateForm.activityType === 'matching' ? (
                <div className="admin-form-wide stack">
                  <p className="helper">{t('Add matching pairs. Learners will match the left side to the right side.')}</p>
                  {templateForm.matchingPairs.map((pair, index) => (
                    <div key={index} className="admin-form-grid" style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 12 }}>
                      <label>{t('Left side')}
                        <input className="input" value={pair.left} onChange={(event) => updateMatchingPair(index, { left: event.target.value })} />
                      </label>
                      <label>{t('Right side')}
                        <input className="input" value={pair.right} onChange={(event) => updateMatchingPair(index, { right: event.target.value })} />
                      </label>
                      {templateForm.matchingPairs.length > 2 ? (
                        <button type="button" className="btn btn-ghost" onClick={() => setTemplateForm((prev) => ({ ...prev, matchingPairs: prev.matchingPairs.filter((_, pairIndex) => pairIndex !== index) }))}>
                          {t('Remove pair')}
                        </button>
                      ) : null}
                    </div>
                  ))}
                  <button type="button" className="btn btn-ghost" onClick={() => setTemplateForm((prev) => ({ ...prev, matchingPairs: [...prev.matchingPairs, emptyMatchPair()] }))}>
                    {t('Add pair')}
                  </button>
                </div>
              ) : null}

              {templateForm.activityType === 'pronunciation_prompt' ? (
                <label className="admin-form-wide">{t('Pronunciation phrases (one per line)')}
                  <textarea className="input" rows={5} value={templateForm.pronunciationPhrases} onChange={(event) => setTemplateForm((prev) => ({ ...prev, pronunciationPhrases: event.target.value }))} />
                </label>
              ) : null}

              <details className="admin-form-wide">
                <summary>{t('Advanced JSON override')}</summary>
                <p className="helper">{t('Optional. If activity JSON is provided, it replaces the structured fields above; answer-key JSON is merged into the generated answer key.')}</p>
                <label>{t('Activity content (JSON, optional)')}
                  <textarea className="input" rows={3} value={templateForm.activityJson} onChange={(event) => setTemplateForm((prev) => ({ ...prev, activityJson: event.target.value }))} placeholder="{ }" />
                </label>
                <label>{t('Answer key (JSON, optional)')}
                  <textarea className="input" rows={3} value={templateForm.answerKeyJson} onChange={(event) => setTemplateForm((prev) => ({ ...prev, answerKeyJson: event.target.value }))} placeholder="{ }" />
                </label>
              </details>
            </>
          )}
        </div>
      </FormModal>

      <FormModal
        title={t('New assignment')}
        description={t('Assign an active template to one or more learners. Learners see the assignment in their Assigned Learning section.')}
        isOpen={showAssignModal}
        onClose={() => setShowAssignModal(false)}
        size="md"
        footer={(
          <>
            <button type="button" className="btn btn-ghost" onClick={() => setShowAssignModal(false)} disabled={assigning}>{t('Cancel')}</button>
            <button type="button" className="btn btn-primary" onClick={submitAssignment} disabled={assigning || assignLoading}>
              {assigning ? t('Assigning...') : t('Assign')}
            </button>
          </>
        )}
      >
        <ErrorBanner message={assignError || undefined} />
        {assignLoading ? <LoadingState label={t('Loading templates and learners...')} /> : (
          <div className="stack">
            <label>{t('Template')}
              <select className="input" value={assignTemplateId} onChange={(event) => setAssignTemplateId(event.target.value)}>
                <option value="">{t('Select an active template')}</option>
                {activeTemplates.map((template) => <option key={template.id} value={template.id}>{template.title} ({titleCase(template.templateKind)})</option>)}
              </select>
            </label>
            {!activeTemplates.length ? <p className="helper">{t('No active templates yet — activate one in the Templates tab first.')}</p> : null}
            <div>
              <span className="helper">{t('Learners')}</span>
              {learners.length ? (
                <div className="stack" style={{ maxHeight: 200, overflowY: 'auto' }}>
                  {learners.map((learner) => {
                    const learnerUserId = String(learner.learnerUserId || '')
                    return (
                      <label key={String(learner.id)} className="button-row" style={{ alignItems: 'center' }}>
                        <input
                          type="checkbox"
                          checked={assignLearnerIds.includes(learnerUserId)}
                          disabled={!learnerUserId}
                          onChange={() => toggleAssignLearner(learnerUserId)}
                        />
                        <span>{String(learner.workerName || learner.learnerName || t('Worker'))} — {String(learner.skillName || '-')}</span>
                      </label>
                    )
                  })}
                </div>
              ) : <p className="helper">{t('No learners with an active learning goal yet.')}</p>}
            </div>
            <label>{t('Due date (optional)')}
              <input className="input" type="date" value={assignDueDate} onChange={(event) => setAssignDueDate(event.target.value)} />
            </label>
            <label>{t('Personalized note (optional)')}
              <textarea className="input" rows={2} value={assignNote} onChange={(event) => setAssignNote(event.target.value)} />
            </label>
            {assignResult ? <p className="helper" style={{ fontWeight: 600 }}>{assignResult}</p> : null}
          </div>
        )}
      </FormModal>
    </section>
  )
}

export default ContentLibraryPanel
